import React from 'react'
import { AlertCircle, RefreshCcw, ChevronDown, ChevronUp, X } from 'lucide-react'
import { Button } from './button'
import { Card } from './card'
import { cn } from '../../lib/utils'

interface ErrorFallbackProps {
  error?: Error | string
  title?: string
  description?: string
  showDetail?: boolean
  onRetry?: () => void
  onDismiss?: () => void
  className?: string
  variant?: 'default' | 'compact' | 'inline'
}

export function ErrorFallback({
  error,
  title = 'Error Text',
  description,
  showDetail = true,
  onRetry,
  onDismiss,
  className,
  variant = 'default',
}: ErrorFallbackProps) {
  const [showDetails, setShowDetails] = React.useState(false)

  // Extract error message
  const errorMessage = typeof error === 'string' ? error : error?.message
  const errorStack = typeof error === 'object' && error?.stack ? error.stack : undefined

  // Default description if not provided
  const finalDescription =
    description ||
    errorMessage ||
    'An unexpected error occurred. Please try again or contact support if the problem persists.'

  if (variant === 'inline') {
    return (
      <div
        className={cn(
          'flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-red-800',
          className
        )}
      >
        <X className="h-4 w-4 flex-shrink-0 text-red-500" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{title}</p>
          <p className="mt-1 truncate text-xs text-red-600">{finalDescription}</p>
        </div>
        {onRetry && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
            className="border-red-300 text-red-700 hover:bg-red-100"
          >
            <RefreshCcw className="h-3 w-3" />
          </Button>
        )}
      </div>
    )
  }

  if (variant === 'compact') {
    return (
      <Card className={cn('border-red-200 bg-red-50', className)}>
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100">
                <X className="h-4 w-4 text-red-500" />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold text-red-800">{title}</h3>
              <p className="mt-1 text-sm text-red-700">{finalDescription}</p>
            </div>
            <div className="flex items-center gap-2">
              {onRetry && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRetry}
                  className="border-red-300 text-red-700 hover:bg-red-100"
                >
                  Retry
                </Button>
              )}
              {showDetail && (errorStack || errorMessage) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDetails(!showDetails)}
                  className="border-red-300 text-red-700 hover:bg-red-100"
                >
                  Detail
                </Button>
              )}
            </div>
          </div>

          {showDetails && (errorStack || errorMessage) && (
            <div className="mt-4 rounded-md bg-red-100 p-3">
              <details className="text-xs text-red-800">
                <summary className="cursor-pointer font-medium">Technical Details</summary>
                <pre className="mt-2 whitespace-pre-wrap break-words font-mono text-xs">
                  {errorStack || errorMessage}
                </pre>
              </details>
            </div>
          )}
        </div>
      </Card>
    )
  }

  // Default variant - matches the design shown in the image
  return (
    <Card className={cn('border-red-200 bg-red-50', className)}>
      <div className="p-6">
        <div className="flex items-start gap-4">
          {/* Error Icon */}
          <div className="flex-shrink-0">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
              <X className="h-5 w-5 text-red-500" />
            </div>
          </div>

          {/* Content */}
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-red-800">{title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-red-700">{finalDescription}</p>
          </div>

          {/* Actions */}
          <div className="flex flex-shrink-0 flex-col gap-2">
            {onDismiss && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onDismiss}
                className="h-auto p-1 text-red-500 hover:bg-red-100"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
            {showDetail && (errorStack || errorMessage) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDetails(!showDetails)}
                className="border-red-300 text-red-700 hover:bg-red-100"
              >
                Detail
                {showDetails ? (
                  <ChevronUp className="ml-1 h-3 w-3" />
                ) : (
                  <ChevronDown className="ml-1 h-3 w-3" />
                )}
              </Button>
            )}
            {onRetry && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRetry}
                className="border-red-300 text-red-700 hover:bg-red-100"
              >
                <RefreshCcw className="mr-2 h-3 w-3" />
                Retry
              </Button>
            )}
          </div>
        </div>

        {/* Collapsible Error Details */}
        {showDetails && (errorStack || errorMessage) && (
          <div className="mt-6 rounded-md bg-red-100 p-4">
            <h4 className="mb-2 text-sm font-medium text-red-800">Technical Details</h4>
            <div className="text-xs text-red-700">
              <div className="mb-2">
                <strong>Error:</strong> {errorMessage}
              </div>
              {errorStack && (
                <details>
                  <summary className="mb-2 cursor-pointer">Stack Trace</summary>
                  <pre className="whitespace-pre-wrap break-words rounded bg-red-200 p-2 font-mono text-xs">
                    {errorStack}
                  </pre>
                </details>
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}

// Preset configurations for common use cases
export const ErrorFallbackPresets = {
  // Network/API errors
  network: (onRetry?: () => void) => (
    <ErrorFallback
      title="Connection Error"
      description="Unable to connect to the server. Please check your internet connection and try again."
      onRetry={onRetry}
    />
  ),

  // Authentication errors
  authentication: (onRetry?: () => void) => (
    <ErrorFallback
      title="Authentication Required"
      description="Your session has expired. Please log in again to continue."
      onRetry={onRetry}
    />
  ),

  // Repository loading errors
  repository: (onRetry?: () => void) => (
    <ErrorFallback
      title="Repository Load Failed"
      description="Unable to load repository data. This might be due to network issues or server problems."
      onRetry={onRetry}
    />
  ),

  // Image upload errors
  upload: (onRetry?: () => void) => (
    <ErrorFallback
      title="Upload Failed"
      description="The image upload could not be completed. Please check the file format and size, then try again."
      onRetry={onRetry}
    />
  ),

  // General application errors
  application: (error: Error, onRetry?: () => void) => (
    <ErrorFallback
      title="Application Error"
      description="An unexpected error occurred in the application. Our team has been notified."
      error={error}
      onRetry={onRetry}
    />
  ),
}
